import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { PortfolioService } from '../../../services/portfolioService';

const SkillCard = ({ skill, index = 0, delay = 0 }) => {
  const getSkillLevelColor = (level) => {
    const colors = {
      beginner: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      intermediate: 'bg-blue-100 text-blue-800 border-blue-200',
      advanced: 'bg-green-100 text-green-800 border-green-200',
      expert: 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors?.[level] || colors?.intermediate;
  };

  const getSkillProgress = (level) => {
    const progress = { beginner: 25, intermediate: 50, advanced: 75, expert: 100 };
    return progress?.[level] || 50;
  };

  const getSkillIcon = (iconName) => {
    const defaultIcons = { 'JavaScript': 'Code', 'React': 'Zap', 'Node.js': 'Server', 'Python': 'Terminal', 'PostgreSQL': 'Database', 'Docker': 'Package', 'AWS': 'Cloud', 'TypeScript': 'FileText', 'GraphQL': 'Share2', 'React Native': 'Smartphone' };
    return iconName || defaultIcons?.[skill?.name] || 'Code';
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: delay + (index * 0.1), ease: 'easeOut' }} whileHover={{ y: -5, transition: { duration: 0.2 } }} className="group relative bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-semibold text-lg transition-transform group-hover:scale-110" style={{ backgroundColor: skill?.color_hex || '#3B82F6' }}>
            <Icon name={getSkillIcon(skill?.icon_name)} size={24} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900 group-hover:text-blue-600 transition-colors">{skill?.name}</h3>
            <p className="text-sm text-slate-500 capitalize">{skill?.category}</p>
          </div>
        </div>

        {/* Featured badge removed per request */}
      </div>

      {skill?.description && <p className="text-sm text-slate-600 mb-4 leading-relaxed">{skill?.description}</p>}

      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700">Proficiency</span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getSkillLevelColor(skill?.level)}`}>{skill?.level?.charAt(0)?.toUpperCase() + skill?.level?.slice(1)}</span>
        </div>

        <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
          <motion.div className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500" initial={{ width: 0 }} animate={{ width: `${getSkillProgress(skill?.level)}%` }} transition={{ duration: 1, delay: delay + (index * 0.1) + 0.3 }} />
        </div>
      </div>

      {skill?.years_experience > 0 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">Experience</span>
          <span className="font-semibold text-slate-900">{skill?.years_experience} year{skill?.years_experience > 1 ? 's' : ''}</span>
        </div>
      )}

      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
    </motion.div>
  );
};

const SkillsGrid = () => {
  const [skillsByCategory, setSkillsByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const loadSkills = async () => {
      try {
        setLoading(true);
        const result = await PortfolioService?.getSkillsByCategory();
        if (result?.error) {
          setError(result?.error);
        } else {
          setSkillsByCategory(result?.data || {});
        }
      } catch (err) {
        setError('Failed to load skills');
      } finally {
        setLoading(false);
      }
    };

    loadSkills();
  }, []);

  const categories = ['all', ...Object.keys(skillsByCategory)];
  const filteredSkills = activeCategory === 'all' ? Object.values(skillsByCategory)?.flat() : skillsByCategory?.[activeCategory] || [];

  if (loading) {
    return (
      <div className="py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)]?.map((_, index) => (
            <div key={index} className="bg-white rounded-xl p-6 shadow-lg animate-pulse">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-slate-200 rounded-lg"></div>
                <div>
                  <div className="h-5 w-24 bg-slate-200 rounded mb-1"></div>
                  <div className="h-4 w-16 bg-slate-100 rounded"></div>
                </div>
              </div>
              <div className="h-4 bg-slate-100 rounded mb-4"></div>
              <div className="h-2 bg-slate-100 rounded mb-2"></div>
              <div className="flex justify-between">
                <div className="h-4 w-16 bg-slate-100 rounded"></div>
                <div className="h-4 w-12 bg-slate-100 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Icon name="AlertCircle" size={48} className="text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Unable to Load Skills</h3>
        <p className="text-slate-600">{error}</p>
      </div>
    );
  }

  return (
    <div>
      {categories?.length > 1 && (
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {categories?.map((category) => (
            <button key={category} onClick={() => setActiveCategory(category)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${activeCategory === category ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'}`}>
              {category === 'all' ? 'All Skills' : category}
              {category !== 'all' && <span className="ml-1 text-xs opacity-75">({skillsByCategory?.[category]?.length || 0})</span>}
            </button>
          ))}
        </div>
      )}

      {filteredSkills?.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredSkills?.map((skill, index) => (
            <SkillCard key={skill?.id} skill={skill} index={index} delay={0.1} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Icon name="Code" size={48} className="text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-600 mb-2">No Skills Found</h3>
          <p className="text-slate-500">No skills available for the selected category.</p>
        </div>
      )}
    </div>
  );
};

export { SkillCard, SkillsGrid };
export default SkillCard;
